import { apiClient } from "../../lib/http/apiClient";
import { adminStorage } from "../admin/service";

// ── Types (mirror backend onboarding flow StepConfig contract) ──────────────

export type FieldType = "text" | "textarea" | "number" | "select" | "multiselect" | "checkbox" | "yesno" | "upload" | "phone" | "offices" | "multiple" | "group" | "date" | "email" | "currency_number" | "gst";

export interface FieldConfig {
  key: string;
  _originalKey?: string;
  label: string;
  info_tip?: string;
  type: FieldType;
  required: boolean;
  options_type?: string | null;
  sub_fields?: FieldConfig[];
}

export type RenderMode = "reserved" | "dynamic";

export interface StepConfig {
  key: string;
  _originalKey?: string;
  label: string;
  title?: string;
  description?: string;
  order: number;
  required: boolean;
  fields: FieldConfig[];
}

export type FlowRole = "seller" | "buyer" | "partner";

export interface FlowDoc {
  role: FlowRole;
  country: string; // ISO2 code or the literal "DEFAULT"
  steps: StepConfig[];
  updated_at: string | null;
}

export interface KeyChange {
  type: "step" | "field";
  old_key: string;
  new_key: string;
  step_key?: string;
}

const BASE = "/api/v1/onboarding/admin/flows";

function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const onboardingFlowsService = {
  /** List all flow docs for a role (always includes the DEFAULT doc). */
  async list(role: FlowRole): Promise<FlowDoc[]> {
    const { data } = await apiClient.get<FlowDoc[]>(BASE, {
      params: { role },
      headers: adminHeaders(),
    });
    return data;
  },

  /** Fetch a single role+country flow doc. */
  async get(role: FlowRole, country: string): Promise<FlowDoc> {
    const { data } = await apiClient.get<FlowDoc>(`${BASE}/${role}/${country}`, {
      headers: adminHeaders(),
    });
    return data;
  },

  /** Upsert the full steps array for a role+country, with optional key_changes to run migrations. */
  async upsert(role: FlowRole, country: string, steps: StepConfig[], keyChanges: KeyChange[] = []): Promise<FlowDoc> {
    const { data } = await apiClient.put<FlowDoc>(
      `${BASE}/${role}/${country}`,
      { steps, key_changes: keyChanges },
      { headers: adminHeaders() },
    );
    return data;
  },

  /** Copy steps from one country doc to another; returns the target doc. */
  async copy(role: FlowRole, fromCountry: string, toCountry: string): Promise<FlowDoc> {
    const { data } = await apiClient.post<FlowDoc>(
      `${BASE}/${role}/copy`,
      { from_country: fromCountry, to_country: toCountry },
      { headers: adminHeaders() },
    );
    return data;
  },

  /** Delete a country override (rejects DEFAULT server-side). */
  async remove(role: FlowRole, country: string): Promise<void> {
    await apiClient.delete(`${BASE}/${role}/${country}`, { headers: adminHeaders() });
  },
};

// ── Public flow + options API (consumed by the live onboarding pages) ───────

/** Option items returned by GET /api/v1/onboarding/options/{type}. */
export type FlowOptionItem = string | { value: string; label: string; description?: string };

export interface FlowOptionsResponse {
  type: string;
  items: FlowOptionItem[];
}

/** Resolved flow returned by GET /api/v1/onboarding/flow. */
export interface ResolvedFlow {
  role: FlowRole;
  country: string;
  steps: StepConfig[];
}

export const onboardingFlowPublicService = {
  /**
   * Resolve the live onboarding flow for a role (+ optional country), with the
   * backend's DEFAULT-country fallback. Steps are sorted by `order` here so
   * callers can rely on positional ordering.
   */
  async getFlow(role: FlowRole, country?: string): Promise<ResolvedFlow> {
    const { data } = await apiClient.get<ResolvedFlow>("/api/v1/onboarding/flow", {
      params: { role, ...(country ? { country } : {}) },
    });
    const steps = [...(data.steps ?? [])].sort((a, b) => a.order - b.order);
    return { ...data, steps };
  },

  /** Fetch the option list for a dynamic select/multiselect field. */
  async getOptions(optionsType: string): Promise<FlowOptionItem[]> {
    if (optionsType === "countries") {
      const { geoService } = await import("../geo/service");
      const countries = await geoService.getCountries();
      return countries.map(c => ({ value: c.name, label: c.name }));
    }
    if (optionsType === "cities" || optionsType === "states") {
      return [];
    }

    const { data } = await apiClient.get<FlowOptionsResponse>(
      `/api/v1/onboarding/options/${optionsType}`,
    );
    return data?.items ?? [];
  },

  /**
   * Upload a document (business license, registration docs, etc.) for a
   * `type: "upload"` onboarding field. Returns the S3 key (to store as the
   * field value) and a presigned read URL (for immediate display).
   */
  async uploadDocument(file: File): Promise<{ key: string; url: string }> {
    const form = new FormData();
    form.append("file", file, file.name);
    const { data } = await apiClient.post<{ key: string; url: string }>(
      "/api/v1/onboarding/upload",
      form,
    );
    return data;
  },

  /**
   * Live GST verify, called on GST-number blur during onboarding (distinct
   * from the submit-time re-verification the backend always does). Used to
   * auto-fill and lock a "gst"-type field's company_name + seed one office
   * before the step is submitted.
   */
  async verifyGstLive(gstin: string): Promise<{
    gstin: string;
    legal_name: string | null;
    office: { country: string; country_code: string; state: string; state_code: string; city: string; address: string; pincode: string } | null;
    details: Record<string, unknown> | null;
    queued: boolean;
    job_id: string | null;
  }> {
    const { data } = await apiClient.get<{
      gstin: string;
      legal_name: string | null;
      office: { country: string; country_code: string; state: string; state_code: string; city: string; address: string; pincode: string } | null;
      details: Record<string, unknown> | null;
      queued: boolean;
      job_id: string | null;
    }>("/api/v1/onboarding/verify-gst", { params: { gstin } });
    return data;
  },

  /**
   * Poll status of a queued GST verification job (see verifyGstLive's
   * `queued`/`job_id`) — the sync call fell through to the background
   * worker, so this is what actually surfaces the completed result once
   * the worker finishes, typically a few seconds later.
   */
  async getGstVerifyJobStatus(jobId: string): Promise<{
    job_id: string;
    status: "queued" | "in_progress" | "completed" | "failed" | "stopped_for_review" | "rejected";
    gstin: string;
    error: string | null;
    legal_name: string | null;
    office: { country: string; country_code: string; state: string; state_code: string; city: string; address: string; pincode: string } | null;
    details: Record<string, unknown> | null;
  }> {
    const { data } = await apiClient.get(`/api/v1/onboarding/gst-verify-jobs/${jobId}`);
    return data;
  },

  /** Resolve a presigned display URL for an already-uploaded onboarding document key. */
  async getDocumentDisplayUrl(key: string): Promise<string> {
    try {
      const { data } = await apiClient.get<{ url: string; key: string; expires_in: number }>(
        `/api/public/bindings/presigned-url?key=${encodeURIComponent(key)}`,
      );
      return data.url;
    } catch {
      return "";
    }
  },
};
